import { memo, ReactElement } from 'react'

import { Typography } from 'antd'
import { Field } from 'formik'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'
import { number, object } from 'yup'

import { Flex } from 'components/core'
import { SingleValueField } from 'components/EditableFields/SharedFields/SingleValueField'
import { FormikInputNumber } from 'components/formik'
import { getCurrencySymbol } from 'configuration/currency'
import { COUNTRY_CURRENCIES } from 'const/codes'
import { UNDEFINED_VALUE } from 'const/formatting'
import { useUpdateMapPrice } from 'dynamicModules/ProductCatalog/hooks/useUpdateMapPrice'
import { formatCurrency } from 'helpers/formatting'

const { Text } = Typography

interface RecordType {
    product_id?: string
    metadata_id?: string
    map_price?: number | null
    currency?: string
}

type FormValues = {
    map_price: number | null
}

type Props<T extends RecordType> = {
    record: T
    rowIndex: number
    updateRecord: (data: { rowIndex: number; record: T }) => void
}

const VALIDATION_SCHEMA = object().shape({
    map_price: number().nullable().min(0),
})

function MapPriceField<T extends RecordType>({
    record,
    rowIndex,
    updateRecord,
}: Props<T>): ReactElement {
    const { t } = useTranslation()
    const { updateMapPrice } = useUpdateMapPrice()

    const productId = record.product_id ?? record.metadata_id ?? ''
    const marketplace = productId.split('_', 1)[0]
    const currency =
        record.currency ??
        COUNTRY_CURRENCIES[marketplace as keyof typeof COUNTRY_CURRENCIES] ??
        ''
    const currencySymbol = getCurrencySymbol(currency)
    const initialValues: FormValues = { map_price: record.map_price ?? null }

    const formatFieldValue = (values: FormValues): string =>
        values.map_price == null
            ? UNDEFINED_VALUE
            : formatCurrency(values.map_price, {
                  currencyCode: currency,
                  decimal: true,
              })

    return (
        <SingleValueField<FormValues>
            fieldName="map_price"
            formatFieldValue={formatFieldValue}
            initialValues={initialValues}
            onSave={(values) => {
                if ((values.map_price || null) === (record.map_price ?? null)) {
                    return Promise.resolve({ status: 200 })
                }
                return updateMapPrice({
                    productId,
                    currency,
                    mapPrice: values.map_price,
                    onSuccess: () =>
                        updateRecord({
                            rowIndex,
                            record: {
                                ...record,
                                map_price: values.map_price || null,
                            },
                        }),
                })
            }}
            validationSchema={VALIDATION_SCHEMA}
        >
            {({ isSubmitting }) => (
                <Flex alignItems="center" gap={8}>
                    <Text>
                        {t('productCatalog:mapInlineEdit.label', 'MAP')}
                    </Text>
                    <Field
                        component={FormikInputNumber}
                        disabled={isSubmitting}
                        prefix={currencySymbol}
                        min={0}
                        name="map_price"
                        step={0.01}
                    />
                </Flex>
            )}
        </SingleValueField>
    )
}

export default memo(MapPriceField, isEqual) as typeof MapPriceField
